import { useId } from 'react';

import IconButton from 'components/partials/button/icon-button';
import Select from 'components/partials/select/select';
import type { SelectOption } from 'components/partials/select/select';

import { SELECT_MENU_Z_INDEX } from 'containers/admin/clients/client/tabs-components/critical-populations/critical-populations.constants';
import type { DimensionBlockModel } from 'containers/admin/clients/client/tabs-components/critical-populations/hooks/use-capture-form';

import Icons from 'assets/icons';

interface DimensionBlockProps {
  block: DimensionBlockModel;
  index: number;
  canRemove: boolean;
  onAttributeChange: (index: number, attribute: string) => void;
  onValuesChange: (index: number, values: string[]) => void;
  onRemove: (index: number) => void;
}

const SELECT_LABEL_STYLES = 'mt-0';

const menuPortalStyles = () => ({
  menuPortal: (base: object) => ({ ...base, zIndex: SELECT_MENU_Z_INDEX }),
});

const multiSelectStyles = () => ({
  ...menuPortalStyles(),
  dropdownIndicator: (base: object) => ({ ...base, display: 'none' }),
});

const toArray = (
  option: SelectOption | SelectOption[] | null
): SelectOption[] => {
  if (!option) return [];

  return Array.isArray(option) ? option : [option];
};

const DimensionBlock = ({
  block,
  index,
  canRemove,
  onAttributeChange,
  onValuesChange,
  onRemove,
}: DimensionBlockProps) => {
  const attributeId = useId();
  const valuesId = useId();
  const position = index + 1;
  const isMulti = !block.definition?.singleSelect;

  const attributeOption = block.attributeOptions.find(
    ({ value }) => value === block.attribute
  );
  const selectedValues: SelectOption[] = block.values.map((value) => ({
    value,
    label: value,
  }));

  return (
    <section
      aria-label={`Dimension ${position}`}
      className="relative flex flex-col gap-3 rounded-md border border-navy-100 p-4"
      data-testid={`dimension-block-${index}`}
    >
      <h3 className="text-base font-bold text-gray-700">Dimension</h3>
      {canRemove && (
        <IconButton
          variant="reserve-text"
          size="sm"
          icon={<Icons.Delete aria-hidden="true" />}
          aria-label={`Remove dimension ${position}`}
          className="absolute right-2 top-2"
          onClick={() => onRemove(index)}
          data-testid={`dimension-block-${index}-remove`}
        />
      )}
      <Select<SelectOption>
        label="Dimension"
        labelFor={attributeId}
        labelInTop
        required
        customClass={SELECT_LABEL_STYLES}
        placeholder=""
        options={block.attributeOptions}
        value={attributeOption}
        fieldError={
          block.attributeError ? { message: block.attributeError } : undefined
        }
        onChange={(option) => {
          const [selected] = toArray(option);

          if (selected) onAttributeChange(index, String(selected.value));
        }}
        menuPortalTarget={document.body}
        extraStyles={menuPortalStyles}
        data-testid={`dimension-block-${index}-attribute`}
      />
      <Select<SelectOption>
        label="Value"
        labelFor={valuesId}
        labelInTop
        required
        isMulti={isMulti}
        isClearable={isMulti}
        customClass={SELECT_LABEL_STYLES}
        placeholder=""
        options={block.valueOptions}
        value={isMulti ? selectedValues : selectedValues[0]}
        fieldError={
          block.valuesError ? { message: block.valuesError } : undefined
        }
        onChange={(option) =>
          onValuesChange(
            index,
            toArray(option).map(({ value }) => String(value))
          )
        }
        menuPortalTarget={document.body}
        extraStyles={isMulti ? multiSelectStyles : menuPortalStyles}
        data-testid={`dimension-block-${index}-values`}
      />
    </section>
  );
};

export default DimensionBlock;
